import { z } from 'zod';

// The LOCK1 QR label batches on `/app_service/panel/v1/qr-batches` (CBX-11715), mirrored from the
// delivered contract. Tolerant, not `.strict()`: the screen renders the fields it names, so an
// additive one is inert.
//
// `status` is a closed enum because the screen switches on it (chip, download, polling); a value
// outside it fails the read rather than rendering a batch the panel cannot describe. `lock_model`
// stays a string: the 2022 batches are CBX10, the screen only prints it, and a new model must not
// blank the table.
export const QrBatchStatusSchema = z.enum(['PENDING', 'DONE', 'FAILED']);

export type QrBatchStatus = z.infer<typeof QrBatchStatusSchema>;

export const QrBatchSchema = z
  .object({
    id: z.string(),
    lock_model: z.string(),
    qr_amount: z.number().int(),
    status: QrBatchStatusSchema,
    created_at: z.string(),
    finished_at: z.string().nullable(),
    has_file: z.boolean(),
    in_use: z.number().int(),
    in_use_percentage: z.number(),
  })
  .transform((batch) => ({
    id: batch.id,
    lockModel: batch.lock_model,
    qrAmount: batch.qr_amount,
    status: batch.status,
    createdAt: batch.created_at,
    finishedAt: batch.finished_at,
    hasFile: batch.has_file,
    inUse: batch.in_use,
    inUsePercentage: batch.in_use_percentage,
  }));

export type QrBatch = z.infer<typeof QrBatchSchema>;

export const QrBatchListSchema = z
  .object({ items: z.array(QrBatchSchema), has_pending: z.boolean() })
  .transform(({ items, has_pending }) => ({ items, hasPending: has_pending }));

export type QrBatchList = z.infer<typeof QrBatchListSchema>;

// The `202` body of `POST /qr-batches`: the batch as it was queued.
export const QrBatchCreatedSchema = z
  .object({
    id: z.string(),
    lock_model: z.string(),
    qr_amount: z.number().int(),
    status: QrBatchStatusSchema,
    created_at: z.string(),
  })
  .transform((batch) => ({
    id: batch.id,
    lockModel: batch.lock_model,
    qrAmount: batch.qr_amount,
    status: batch.status,
    createdAt: batch.created_at,
  }));

export type QrBatchCreated = z.infer<typeof QrBatchCreatedSchema>;

// How much of the code space the generator has used, on `GET /qr-batches/stats`.
export const QrBatchStatsSchema = z
  .object({
    alphabet: z.string(),
    code_length: z.number().int(),
    total_space: z.number(),
    generated: z.number().int(),
    in_use: z.number().int(),
    unused: z.number().int(),
    space_used_percentage: z.number(),
    in_use_percentage: z.number(),
    remaining: z.number(),
  })
  .transform((stats) => ({
    alphabet: stats.alphabet,
    codeLength: stats.code_length,
    totalSpace: stats.total_space,
    generated: stats.generated,
    inUse: stats.in_use,
    unused: stats.unused,
    spaceUsedPercentage: stats.space_used_percentage,
    inUsePercentage: stats.in_use_percentage,
    remaining: stats.remaining,
  }));

export type QrBatchStats = z.infer<typeof QrBatchStatsSchema>;
